import * as THREE from "three";

export interface RendererConfig {
  canvas: HTMLCanvasElement;
  alpha?: boolean;
  clearColor?: number;
}

/**
 * Singleton WebGL renderer with the project's canonical configuration:
 *  - ACES Filmic tone mapping (cinematic highlight rolloff)
 *  - sRGB output color space
 *  - DPR capped at 1.5 desktop / 1.0 touch
 *  - high-performance hint
 *  - Antialiasing via SMAA pass in postprocessing (not the renderer's built-in MSAA)
 */
export function createRenderer({
  canvas,
  alpha = false,
  clearColor = 0x06140f,
}: RendererConfig): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha,
    antialias: false, // SMAA handles this in postprocessing — cheaper and better
    powerPreference: "high-performance",
    stencil: false,
    depth: true,
  });

  // DPR strategy: touch devices get 1.0 (battery/heat); desktop capped at 1.5
  const isCoarse = matchMedia("(pointer: coarse)").matches;
  const targetDpr = isCoarse ? 1.0 : Math.min(window.devicePixelRatio || 1, 1.5);
  renderer.setPixelRatio(targetDpr);

  renderer.setSize(window.innerWidth, window.innerHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;

  if (!alpha) {
    renderer.setClearColor(clearColor, 1);
  }

  return renderer;
}

/**
 * Handle resize with debounce on the next animation frame.
 * Returns the dispose function.
 */
export function attachResize(
  renderer: THREE.WebGLRenderer,
  camera: THREE.PerspectiveCamera,
  onResize?: (w: number, h: number) => void,
): () => void {
  let raf = 0;
  const handler = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      onResize?.(w, h);
    });
  };
  window.addEventListener("resize", handler, { passive: true });
  return () => {
    window.removeEventListener("resize", handler);
    cancelAnimationFrame(raf);
  };
}
